import { EllipsisVertical, LogOut, Monitor, Moon, Sun, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";

import { initialsFromName } from "@/lib/utils";
import type { AuthUser } from "@/types/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export function NavUser({
  user,
  variant = "pill",
}: {
  user: AuthUser;
  variant?: "pill" | "flat";
}) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  async function handleLogout() {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/login", { replace: true });
      window.location.reload();
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={
                variant === "flat"
                  ? "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
                  : "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border-[0.5px] border-border cursor-pointer"
              }
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.image ?? undefined} alt={user.name ?? "User"} />
                <AvatarFallback className="rounded-lg">
                  {initialsFromName(user.name ?? user.email ?? "Landnest")}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {user.name ?? user.email ?? "Landnest User"}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {user.email ?? user.role}
                </span>
              </div>
              <EllipsisVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="end"
            sideOffset={8}
            className="w-56 rounded-lg p-1 z-[100]"
          >
            {/* User info header */}
            <div className="flex items-center gap-2 px-2 py-2 text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.image ?? undefined} alt={user.name ?? "User"} />
                <AvatarFallback className="rounded-lg">
                  {initialsFromName(user.name ?? user.email ?? "Landnest")}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium">
                  {user.name ?? user.email ?? "Landnest User"}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {user.email ?? user.role}
                </span>
              </div>
            </div>

            <Separator edgeToEdge="1" className="my-1" />

            <button
              onClick={() => navigate(user.role === "PARTNER" ? "/partner/account" : "/profile")}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            >
              <UserCog className="size-4 text-muted-foreground" />
              Account
            </button>

            <Separator edgeToEdge="1" className="my-1" />

            <div className="px-2 py-1.5">
              <p className="text-xs text-muted-foreground mb-1.5">Theme</p>
              <div className="flex gap-1">
                {([
                  { value: "light", icon: Sun },
                  { value: "dark", icon: Moon },
                  { value: "system", icon: Monitor },
                ] as const).map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setTheme(value)}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-sm py-1 text-xs transition-colors capitalize ${
                      theme === value
                        ? "bg-accent text-accent-foreground font-medium"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    <Icon className="size-3" />
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <Separator edgeToEdge="1" className="my-1" />

            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            >
              <LogOut className="size-4 text-muted-foreground" />
              Log out
            </button>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
